import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { shotKeys } from "./shots/shotKeys";

type AddMessageListener = (listener: (message: any) => void) => () => void;

/**
 * Listens for activity.* WebSocket events and invalidates relevant
 * TanStack Query caches so the UI stays in sync in real time.
 *
 * Must be mounted alongside useWebSocket() to receive events.
 */
export function useActivityEvents(
  addMessageListener: AddMessageListener | undefined,
) {
  const queryClient = useQueryClient();
  const usageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!addMessageListener) return;

    const removeListener = addMessageListener((message) => {
      const eventType = message?.eventType as string | undefined;
      if (!eventType) return;

      // Organisation usage: listen for execution events from orchestrator
      // Throttled: waits 2s to allow all related DB writes to settle
      if (
        eventType === "workflow.completed" ||
        eventType === "workflow.failed" ||
        eventType === "workflow.cancelled"
      ) {
        if (usageTimerRef.current === null) {
          usageTimerRef.current = setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ["organisation-usage"] });
            usageTimerRef.current = null;
          }, 2_000);
        }
      }

      // Everything below is activity.* events only
      if (!eventType.startsWith("activity.")) return;

      const slug = message.eventData?.eventTypeSlug as string | undefined;
      const projectId = message.eventData?.projectId as string | undefined;
      const workflowId = message.eventData?.workflowId as string | undefined;
      const shotId = message.eventData?.shotId as string | undefined;
      const organisationId = message.eventData?.organisationId as
        string | undefined;

      // Always invalidate recent-work (hero card) and activity feed
      queryClient.invalidateQueries({ queryKey: ["recent-work"] });
      queryClient.invalidateQueries({ queryKey: ["activity-feed"] });

      // Project-level events
      if (
        slug?.startsWith("project-") ||
        slug?.startsWith("share-") ||
        slug === "workflow-created" ||
        slug === "asset-confirmed" ||
        slug === "asset-deleted" ||
        slug === "asset-undeleted" ||
        slug === "asset-purged"
      ) {
        queryClient.invalidateQueries({ queryKey: ["projects"] });
      }

      // Asset events: REMOVED - useUnifiedEventConsumer already handles asset
      // invalidation with proper debouncing (500ms). Keeping this here caused
      // duplicate rapid-fire calls during video chunking (10+ calls in quick
      // succession). See investigation 2026-04-27.

      // Workflow events
      if (slug?.startsWith("workflow-") && projectId) {
        queryClient.invalidateQueries({ queryKey: ["workflows"] });
      }
      if (slug?.startsWith("workflow-") && workflowId) {
        queryClient.invalidateQueries({ queryKey: ["workflow", workflowId] });
      }

      // Shot Builder events (shot-*, scene-*): keep scene list, shot detail,
      // and take list in sync for live multi-user updates.
      if (slug?.startsWith("shot-") || slug?.startsWith("scene-")) {
        if (projectId) {
          queryClient.invalidateQueries({
            queryKey: shotKeys.scenes(projectId),
          });
        }
        if (shotId) {
          queryClient.invalidateQueries({ queryKey: shotKeys.shot(shotId) });
          queryClient.invalidateQueries({ queryKey: shotKeys.assets(shotId) });
        }
      }

      // Timeline events
      if (slug?.startsWith("timeline-")) {
        queryClient.invalidateQueries({ queryKey: ["timeline"] });
      }

      // Organisation events
      if (slug?.startsWith("organisation-") && organisationId) {
        queryClient.invalidateQueries({ queryKey: ["organisation"] });
        queryClient.invalidateQueries({ queryKey: ["organisation-members"] });
        queryClient.invalidateQueries({ queryKey: ["organisation-roles"] });
      }

      // Organisation usage: also refresh on asset-confirmed activity events
      if (slug === "asset-confirmed") {
        if (usageTimerRef.current === null) {
          usageTimerRef.current = setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ["organisation-usage"] });
            usageTimerRef.current = null;
          }, 2_000);
        }
      }

      // Team events
      if (slug === "user-joined-team" || slug === "user-left-team") {
        queryClient.invalidateQueries({ queryKey: ["projects"] });
      }
    });

    return () => {
      removeListener();
      if (usageTimerRef.current !== null) {
        clearTimeout(usageTimerRef.current);
        usageTimerRef.current = null;
      }
    };
  }, [addMessageListener, queryClient]);
}
